<template>
    <!-- Navbar start -->
    <nav 
        :class="{'bg-black/95 shadow-2xl': isScrolled || showMenu, 'bg-transparent': !isScrolled && !showMenu }" 
        class="w-full fixed top-0 left-0 right-0 z-50 transition-all duration-300"
    >
        <div class="max-w-7xl mx-auto py-4 px-6 md:px-12 flex justify-between items-center w-full relative">
            <div class="flex items-center">
                <a @click="showMenu = false" href="#main" class="flex items-center gap-2 group">
                    <Motion as="img"
                        :initial="{ scale: 0, opacity: 0 }"
                        :animate="{ scale: 1, opacity: 1 }"
                        :transition="{ type: 'spring' }"
                        :src="require('../assets/vivstream-logo.png')" class="h-10 md:h-12 w-auto object-contain" alt="logo" />
                    <div class="flex items-center text-xl md:text-2xl font-black italic tracking-tighter">
                        <span class="text-white uppercase">VIV</span>
                        <span class="text-red-500 uppercase ml-1">STREAMS</span>
                    </div>
                </a>
            </div>

            <!-- Desktop Menu -->
            <div class="hidden lg:flex items-center absolute left-1/2 -translate-x-1/2 gap-10">
                <a href="#promotions" @click="showMenu = false" class="text-sm font-bold uppercase tracking-widest text-gray-400 hover:text-white transition-all duration-300">Promotions</a>
                <a href="#leaderboard" @click="showMenu = false" class="text-sm font-bold uppercase tracking-widest text-gray-400 hover:text-white transition-all duration-300">Leaderboards</a>
                <a href="#socials" @click="showMenu = false" class="text-sm font-bold uppercase tracking-widest text-gray-400 hover:text-white transition-all duration-300">Socials</a>
            </div>

            <!-- Right Side: CTA Button -->
            <div class="flex items-center gap-4">
                <a href="https://discord.com/invite/wXEZRuUnJP" target="_blank" class="hidden md:block bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white px-8 py-2 md:py-2.5 rounded-full font-bold text-sm uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(220,38,38,0.4)] active:scale-95">
                    Join Now
                </a>

                <!-- Mobile Toggle -->
                <button class="lg:hidden text-white" @click="toggleMenu()">
                    <svg v-if="!showMenu" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-8 h-8">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-8 h-8">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- Mobile Menu Overlay -->
            <transition 
                enter-active-class="transition duration-500 ease-in-out"
                enter-from-class="translate-x-full"
                enter-to-class="translate-x-0"
                leave-active-class="transition duration-500 ease-in-out"
                leave-from-class="translate-x-0"
                leave-to-class="translate-x-full"
            >
                <div v-if="showMenu" class="fixed inset-0 w-full h-screen bg-black z-[100] flex flex-col lg:hidden">
                    <!-- Mobile Menu Header -->
                    <div class="flex justify-between items-center py-4 px-6 border-b border-white/5">
                        <div class="flex items-center gap-2">
                            <img src="../assets/vivstream-logo.png" class="h-10 w-auto object-contain" alt="logo">
                            <div class="flex items-center text-xl font-black italic tracking-tighter">
                                <span class="text-white uppercase">VIV</span>
                                <span class="text-red-500 uppercase ml-1">STREAMS</span>
                            </div>
                        </div>
                        <button @click="showMenu = false" class="text-white p-2 border border-white/20 rounded-lg">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-8 h-8">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <!-- Mobile Menu Links -->
                    <div class="flex-1 flex flex-col items-center justify-center gap-10">
                        <a href="#promotions" @click="showMenu = false" class="text-white uppercase font-black text-2xl tracking-[0.2em] hover:text-red-500 transition-colors">Promotions</a>
                        <a href="#leaderboard" @click="showMenu = false" class="text-white uppercase font-black text-2xl tracking-[0.2em] hover:text-red-500 transition-colors">Leaderboards</a>
                        <a href="#socials" @click="showMenu = false" class="text-white uppercase font-black text-2xl tracking-[0.2em] hover:text-red-500 transition-colors">Socials</a>
                        
                        <a href="https://discord.com/invite/wXEZRuUnJP" target="_blank" @click="showMenu = false" class="mt-4 bg-red-600 text-white px-16 py-4 rounded-full font-black uppercase tracking-widest text-lg shadow-[0_0_30px_rgba(220,38,38,0.4)] active:scale-95 text-center">
                            Join Now
                        </a>
                    </div>
                </div>
            </transition>
        </div>
    </nav>
    <!-- Navbar end -->
</template>


<script>
import { Motion } from 'motion-v'

export default {
    components: {
        Motion
    },
    data() {
        return {
            isScrolled: false,
            isLogout: false,
            showMenu: false,
        };
    },
    methods: {
        handleScroll() {
            this.isScrolled = window.scrollY > 0;

			if (this.showMenu && window.innerWidth < 768) {
                this.isScrolled = true;
            }
        },
		toggleMenu() {
            this.showMenu = !this.showMenu;
            if (this.showMenu && window.innerWidth < 768) {
                this.isScrolled = true;
            } else if (!this.showMenu && window.scrollY === 0) {
                this.isScrolled = false;
            }
        },
        isMobile() {
            return window.innerWidth < 1024; // Adjust based on your 'lg' breakpoint in Tailwind CSS config
        },
    },
    mounted() {
        window.addEventListener('scroll', this.handleScroll);
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
    }
}
</script>

<style scoped>

</style>